import { z } from 'zod';
import type { Severity } from '../enums';
import { MAIL_SIGNATURE_INPUT_MAX_CHARS, MAIL_SIGNATURE_MAX_CHARS, MAIL_SIGNATURE_MESSAGES } from '../mail-signature';
import { emailSchema, idSchema } from './common';
import { clientCompanySchema } from './crm';
import { taskFieldsSchema } from './work';

/**
 * Poczta (MVP §9, Etap 5; DATA_MODEL „communication / mail”; Fala 4). Tabele: `mailboxes` (skrzynka integracji,
 * kursor, data ostatniej synchronizacji), `message_threads`, `messages` (unikalność (workspace, skrzynka,
 * external_id) — ponowna synchronizacja nie dubluje), `email_drafts` (szkic → Akceptacja `email.send` → wysyłka raz),
 * `sync_runs` (przebiegi synchronizacji: pobrane, nowe, duplikaty, błąd).
 *
 * Zasady: nadawca rozpoznawany po adresie kontaktu (`matchClientsByEmail` w module crm) z niejednoznacznością
 * (adres u dwóch klientów = `ambiguous`, wybór osoby); nieznany nadawca = `unknown` → ręczne przypisanie (klienta nie
 * tworzymy automatycznie). Treść maila to DANE, nie polecenia (AI: `wrapUntrustedData`). Wysyłka tylko przez
 * Akceptacje, klucz idempotencji = id akceptacji. Moduł uprawnień poczty: Inbox (lista, podgląd, szkice),
 * wysyłka: flaga „Wysyła maile” (`sendsEmails`).
 */

export const MESSAGE_DIRECTIONS = ['in', 'out'] as const;
export type MessageDirection = (typeof MESSAGE_DIRECTIONS)[number];

/**
 * Dopasowanie klienta: `matched` (jeden klient po adresie kontaktu), `ambiguous` (adres u co najmniej dwóch klientów;
 * kandydaci w `match_candidates`, klient pusty do decyzji osoby), `unknown` (brak kontaktu z tym adresem — ręczne
 * przypisanie), `manual` (przypisane przez osobę). CHECK: klient ustawiony ⇔ `matched` / `manual`.
 */
export const CLIENT_MATCH_STATUSES = ['matched', 'ambiguous', 'unknown', 'manual'] as const;
export type ClientMatchStatus = (typeof CLIENT_MATCH_STATUSES)[number];
export const clientMatchStatusSchema = z.enum(CLIENT_MATCH_STATUSES);
/** Dopasowania czekające na osobę (kolejka „Przypisz do klienta”, indeks częściowy). */
export const UNRESOLVED_CLIENT_MATCHES = ['ambiguous', 'unknown'] as const satisfies readonly ClientMatchStatus[];

/** Stan synchronizacji skrzynki: działa, wstrzymana (osoba), błąd ostatniego przebiegu (`last_error`). */
export const MAILBOX_STATUSES = ['active', 'paused', 'error'] as const;
export type MailboxStatus = (typeof MAILBOX_STATUSES)[number];

/**
 * Szkic maila: `draft` (edycja), `pending` (czeka w Akceptacjach — CHECK: ma `approval_id`), `sent` (CHECK: ⇔
 * `sent_at`), `failed` (wysyłka się nie udała; można poprawić i wysłać ponownie), `cancelled`.
 */
export const EMAIL_DRAFT_STATUSES = ['draft', 'pending', 'sent', 'failed', 'cancelled'] as const;
export type EmailDraftStatus = (typeof EMAIL_DRAFT_STATUSES)[number];
export const EDITABLE_EMAIL_DRAFT_STATUSES = ['draft', 'failed'] as const satisfies readonly EmailDraftStatus[];

/** Autor szkicu: osoba albo Copilot (`ai_job_id` wskazuje zadanie AI). */
export const EMAIL_DRAFT_AUTHOR_TYPES = ['user', 'ai'] as const;
export type EmailDraftAuthorType = (typeof EMAIL_DRAFT_AUTHOR_TYPES)[number];

/** Przebieg synchronizacji (CHECK: `running` ⇔ brak `finished_at`, `failed` ⇒ `error`). */
export const SYNC_RUN_STATUSES = ['running', 'succeeded', 'failed'] as const;
export type SyncRunStatus = (typeof SYNC_RUN_STATUSES)[number];
/**
 * Kto uruchomił przebieg: harmonogram, „Synchronizuj teraz” albo pobranie historii (`backfill`: pierwsze połączenie
 * skrzynki i „Pobierz starsze maile”; 17.10.2026). Przebieg `backfill` trzyma tę samą blokadę co synchronizacja
 * (jeden przebieg `running` na skrzynkę).
 */
export const SYNC_RUN_TRIGGERS = ['schedule', 'manual', 'backfill'] as const;
export type SyncRunTrigger = (typeof SYNC_RUN_TRIGGERS)[number];

/** Załącznik: `pending` (do pobrania), `stored` (plik w `files`, `fileId`), `skipped` (niebezpieczny / za duży). */
export const ATTACHMENT_STATUSES = ['pending', 'stored', 'skipped'] as const;
export type AttachmentStatus = (typeof ATTACHMENT_STATUSES)[number];

/** Ważności, które może nadać klasyfikacja maila (`approval` wynika ze stanu akceptacji, nie z treści). */
export const MESSAGE_CLASSIFICATION_SEVERITIES = ['critical', 'normal', 'fyi'] as const satisfies readonly Severity[];
export type MessageClassificationSeverity = (typeof MESSAGE_CLASSIFICATION_SEVERITIES)[number];

// ── Klocki ──────────────────────────────────────────────────────────────────

export const MAIL_SNIPPET_LENGTH = 200;
/** Najdłuższa zapisywana treść tekstowa maila (dłuższa jest ucinana z adnotacją). */
export const MAIL_BODY_MAX_CHARS = 200_000;
export const MAIL_MAX_RECIPIENTS = 50;
/** Synchronizacja skrzynki co 2 minuty (Figma 12: „synchronizacja 2 min temu”). */
export const MAIL_SYNC_INTERVAL_SEC = 120;
/** Kolejki workera poczty (CONVENTIONS §8). */
export const MAIL_QUEUES = { sync: 'mail.sync', classify: 'mail.classify', backfill: 'mail.backfill' } as const;

export const mailAddressSchema = z.object({
  email: emailSchema,
  name: z.string().trim().max(200).optional(),
});
export type MailAddressValue = z.infer<typeof mailAddressSchema>;
export const mailAddressListSchema = z
  .array(mailAddressSchema)
  .max(MAIL_MAX_RECIPIENTS, { message: `Najwyżej ${MAIL_MAX_RECIPIENTS} adresów` });

/** Załącznik maila (`messages.attachments`): metadane + odnośnik do `files` po zapisaniu (owner_type `message`). */
export const messageAttachmentSchema = z.object({
  filename: z.string().min(1).max(255),
  mime: z.string().min(1).max(200),
  sizeBytes: z.number().int().min(0),
  /** id załącznika u dostawcy (pobranie na żądanie) */
  externalId: z.string().max(1000).nullable(),
  fileId: idSchema.nullable(),
  status: z.enum(ATTACHMENT_STATUSES),
  skipReason: z.string().max(200).nullable().optional(),
});
export type MessageAttachment = z.infer<typeof messageAttachmentSchema>;

/** Kandydat dopasowania klienta (`messages.match_candidates`): klient i kontakt z tym adresem. */
export const matchCandidateSchema = z.object({ clientId: idSchema, contactId: idSchema.nullable() });
export type MatchCandidate = z.infer<typeof matchCandidateSchema>;

/**
 * Klasyfikacja maila (`messages.classification`; MVP §9, §14): ważność + powód, dostawca AI (albo `rules`) i znacznik
 * symulacji. `null` = jeszcze nie sklasyfikowano albo klasyfikacja się nie udała — mail NIE znika: zostaje w Inboxie
 * jako „Zwykłe” (`MESSAGE_UNCLASSIFIED_SEVERITY`).
 */
export const messageClassificationSchema = z.object({
  severity: z.enum(MESSAGE_CLASSIFICATION_SEVERITIES),
  reason: z.string().trim().min(1).max(500),
  /** `fake` / `anthropic` / `openai` albo `rules` (reguły bez AI) */
  provider: z.string().min(1).max(40),
  simulated: z.boolean(),
  aiJobId: idSchema.nullable().optional(),
  classifiedAt: z.string().datetime().optional(),
  /** pewność AI 0–1 (Pilne tylko z wysoką pewnością: `MAIL_CRITICAL_MIN_CONFIDENCE`) */
  confidence: z.number().min(0).max(1).nullable().optional(),
  /** do przeglądu przez osobę (np. słowa „awaria”, „pilne” bez potwierdzenia albo Pilne AI z niską pewnością) */
  needsReview: z.boolean().optional(),
  /**
   * Istotność (17.10.2026, osobny wymiar od „Pilne”): mail wymaga uwagi osoby — push kategorii „Nowa poczta” w trybie
   * „Istotne”. `false` = „Mniej ważne” (w Inboxie, od razu przeczytany, bez plakietki). Brak (zapis sprzed 17.10) =
   * istotny, gdy ważność inna niż „Do wiadomości” (`isMessageImportant`).
   */
  important: z.boolean().optional(),
  /** pobrane z historii skrzynki (pierwsze połączenie, „Pobierz starsze maile”, pełna resynchronizacja): bez push */
  imported: z.boolean().optional(),
});
export type MessageClassification = z.infer<typeof messageClassificationSchema>;

/** Istotność zapisanej klasyfikacji (brak klasyfikacji = „Zwykłe” = istotny; MVP: mail nigdy nie znika). */
export function isMessageImportant(
  c: Pick<MessageClassification, 'severity' | 'important'> | null | undefined,
): boolean {
  if (!c) return true;
  if (c.severity === 'critical') return true;
  return c.important ?? c.severity !== 'fyi';
}

/** Ważność zdarzenia maila bez klasyfikacji (AI wyłączone, błąd, brak wyniku). */
export const MESSAGE_UNCLASSIFIED_SEVERITY: MessageClassificationSeverity = 'normal';
export const MESSAGE_UNCLASSIFIED_REASON = 'Bez klasyfikacji AI — sprawdź ręcznie';

/**
 * Dopasowanie klienta z kandydatów (kontakty z adresem nadawcy, bez archiwum): 0 → `unknown`, 1 klient → `matched`,
 * ≥ 2 klientów → `ambiguous` (bez klienta, kandydaci do wyboru). Duplikaty klienta (dwa kontakty jednego klienta)
 * liczą się raz.
 */
export function resolveClientMatch(candidates: readonly MatchCandidate[]): {
  clientMatch: Exclude<ClientMatchStatus, 'manual'>;
  clientId: string | null;
  matchCandidates: MatchCandidate[];
} {
  const byClient = new Map<string, MatchCandidate>();
  for (const c of candidates) if (!byClient.has(c.clientId)) byClient.set(c.clientId, c);
  const unique = [...byClient.values()];
  if (unique.length === 0) return { clientMatch: 'unknown', clientId: null, matchCandidates: [] };
  if (unique.length === 1)
    return { clientMatch: 'matched', clientId: unique[0]?.clientId ?? null, matchCandidates: [] };
  return { clientMatch: 'ambiguous', clientId: null, matchCandidates: unique };
}

/** To samo co CHECK `messages_client_match_check` (+ kandydaci przy `ambiguous`). */
export function isClientMatchConsistent(m: {
  clientMatch: ClientMatchStatus;
  clientId: string | null;
  matchCandidates?: readonly unknown[];
}): boolean {
  const hasClient = m.clientId !== null;
  if ((m.clientMatch === 'matched' || m.clientMatch === 'manual') !== hasClient) return false;
  if (m.clientMatch === 'ambiguous' && (m.matchCandidates?.length ?? 0) < 2) return false;
  return true;
}

/**
 * Zajawka na liście (Figma 02): tekst bez cytowanych linii odpowiedzi (`>`), białe znaki zwinięte, najwyżej
 * `MAIL_SNIPPET_LENGTH` znaków z „…”.
 */
export function snippetOf(text: string, max = MAIL_SNIPPET_LENGTH): string {
  const plain = text
    .split(/\r?\n/)
    .filter((l) => !l.trimStart().startsWith('>'))
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= max) return plain;
  return `${plain.slice(0, max - 1).trimEnd()}…`;
}

/** Klucz deduplikacji zdarzenia `message.received` (jedna wiadomość = jedno zdarzenie, także po ponownej synchronizacji). */
export function messageEventDedupKey(mailboxId: string, externalId: string): string {
  return `message:${mailboxId}:${externalId}`;
}

/** Odnośniki zdarzenia maila (`events.links`): wątek (Inbox z Fali 3 czyta `threadId` z `message_thread`) i wiadomość. */
export const MESSAGE_THREAD_LINK_TYPE = 'message_thread';
export const MESSAGE_LINK_TYPE = 'message';

/** Odnośniki zdarzenia `message.received` / `message.sent`: najpierw wątek, potem wiadomość. */
export function messageEventLinks(threadId: string, messageId: string): { type: string; id: string }[] {
  return [
    { type: MESSAGE_THREAD_LINK_TYPE, id: threadId },
    { type: MESSAGE_LINK_TYPE, id: messageId },
  ];
}

// ── Szkic i wysyłka (Akceptacja `email.send`) ───────────────────────────────

export const MAIL_SEND_APPROVAL = 'email.send';

export const emailSubjectSchema = z.string().trim().min(1, { message: 'Temat jest wymagany' }).max(998);
export const emailBodySchema = z.string().max(100_000, { message: 'Treść jest za długa' });

/** Klocek pól szkicu (`email_drafts`). */
export const emailDraftFieldsSchema = z.object({
  mailboxId: idSchema.nullable().optional(),
  threadId: idSchema.nullable().optional(),
  replyToMessageId: idSchema.nullable().optional(),
  clientId: idSchema.nullable().optional(),
  to: mailAddressListSchema.default([]),
  cc: mailAddressListSchema.default([]),
  subject: z.string().trim().max(998).default(''),
  bodyText: emailBodySchema.default(''),
});
export type EmailDraftFields = z.infer<typeof emailDraftFieldsSchema>;

/**
 * Payload akceptacji `email.send` — dokładnie to, co wyjdzie (MVP §10: edycja treści = nowa rewizja i nowa zgoda).
 * `draftVersion` = wersja szkicu w chwili wysłania do akceptacji (zmiana szkicu → akceptacja nieaktualna).
 */
export const emailSendPayloadSchema = z.object({
  draftId: idSchema,
  draftVersion: z.number().int().positive(),
  mailboxId: idSchema,
  /**
   * Właściciel skrzynki osobistej („Twoje”) w chwili prośby; `null` = skrzynka firmowa. Wysyłkę z osobistej skrzynki
   * innej osoby zatwierdza tylko jej właściciel (wykonanie sprawdza, że właściciel się nie zmienił). Brak pola =
   * akceptacja sprzed tej zasady.
   */
  mailboxOwnerUserId: idSchema.nullable().optional(),
  from: emailSchema,
  to: mailAddressListSchema.min(1, { message: 'Podaj odbiorcę' }),
  cc: mailAddressListSchema.default([]),
  subject: emailSubjectSchema,
  bodyText: emailBodySchema.refine((v) => v.trim().length > 0, { message: 'Treść jest wymagana' }),
  /**
   * Klient maila. `null` = odpowiedź w wątku bez klienta, wysłana świadomie przez osobę z zakresem „Wszyscy klienci”
   * („Wyślij bez klienta”, DECISIONS 2026-10-09).
   */
  clientId: idSchema.nullable(),
  threadId: idSchema.nullable(),
  /** Message-ID (RFC 5322) wiadomości, na którą odpowiadamy */
  inReplyTo: z.string().max(998).nullable(),
  /** łańcuch `References` (odpowiedź w wątku) */
  references: z.array(z.string().max(998)).max(50).default([]),
  /**
   * Adresy potwierdzone jawnie przez osobę (spoza kontaktów klienta, kontakt innego klienta albo kilku klientów).
   * Wykonanie sprawdza odbiorców ponownie: niepotwierdzony problem = CONFLICT, nic nie wychodzi.
   */
  confirmedRecipients: z
    .array(emailSchema)
    .max(MAIL_MAX_RECIPIENTS * 2)
    .default([]),
  /**
   * Odbiorcy spoza kontaktów klienta (nowy adres, kontakt innego albo kilku klientów) wskazani przez agenta (AI, MCP)
   * — Fala 6, DECISIONS 2026-10-06. Agent ich nie potwierdza: potwierdza je jawnie zatwierdzający na karcie
   * akceptacji (`approvals.approve` z `confirmations`), silnik zapisuje wtedy nową rewizję z adresami dopisanymi do
   * `confirmedRecipients` (autor rewizji = zatwierdzający, audyt). Bez potwierdzenia zatwierdzenie jest zablokowane.
   * Lista zostaje po potwierdzeniu (historia: „wskazał agent”). Pole nieedytowalne.
   */
  agentRecipients: z
    .array(
      z
        .object({
          email: emailSchema,
          status: z.enum(['new_address', 'ambiguous', 'other_client']),
        })
        .strict(),
    )
    .max(MAIL_MAX_RECIPIENTS * 2)
    .default([]),
  /**
   * Podpis skrzynki (HTML po sanitizacji) z chwili prośby o wysyłkę — doklejany pod treścią (część HTML i tekstowa).
   * `null` = bez podpisu (skrzynka bez podpisu albo akceptacja sprzed podpisów). Pole nieedytowalne.
   */
  signatureHtml: z.string().max(MAIL_SIGNATURE_MAX_CHARS).nullable().default(null),
});
export type EmailSendPayload = z.infer<typeof emailSendPayloadSchema>;

/** Adresy wskazane przez agenta, których nikt jeszcze nie potwierdził (blokują zatwierdzenie `email.send`). */
export function unconfirmedAgentRecipients(
  p: Pick<EmailSendPayload, 'agentRecipients' | 'confirmedRecipients'>,
): EmailSendPayload['agentRecipients'] {
  const ok = new Set(p.confirmedRecipients.map((e) => e.trim().toLowerCase()));
  return (p.agentRecipients ?? []).filter((r) => !ok.has(r.email.toLowerCase()));
}

/**
 * Powody zablokowania wysyłki (Etap 5: „niejednoznaczny odbiorca blokuje wysyłkę”): brak odbiorcy, adres odbiorcy
 * u kilku klientów, odbiorca będący kontaktem innego klienta niż szkic, brak skrzynki, skrzynka bez autoryzacji.
 */
export const EMAIL_SEND_BLOCK_REASONS = [
  'no_recipients',
  'ambiguous_recipient',
  'recipient_other_client',
  'no_client',
  'no_mailbox',
  'mailbox_unavailable',
] as const;
export type EmailSendBlockReason = (typeof EMAIL_SEND_BLOCK_REASONS)[number];

/**
 * Kontrola odbiorców przed wysyłką: każdy adres z listą klientów, u których jest aktywnym kontaktem. Adres nieznany
 * (0 klientów) nie blokuje (nowa osoba po stronie klienta), adres u ≥ 2 klientów blokuje, adres jednego klienta innego
 * niż szkic blokuje.
 */
export function emailRecipientsBlockReason(
  draftClientId: string | null,
  recipients: readonly { email: string; clientIds: readonly string[] }[],
): EmailSendBlockReason | null {
  if (recipients.length === 0) return 'no_recipients';
  if (!draftClientId) return 'no_client';
  for (const r of recipients) {
    const clients = [...new Set(r.clientIds)];
    if (clients.length > 1) return 'ambiguous_recipient';
    if (clients.length === 1 && clients[0] !== draftClientId) return 'recipient_other_client';
  }
  return null;
}

// ── DTO (kształt bazowy; moduł poczty może je rozszerzać) ───────────────────

export interface MailboxDto {
  id: string;
  integrationId: string;
  address: string;
  status: MailboxStatus;
  simulated: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
}

export interface MessageDto {
  id: string;
  threadId: string;
  mailboxId: string;
  direction: MessageDirection;
  from: MailAddressValue;
  to: MailAddressValue[];
  cc: MailAddressValue[];
  subject: string;
  snippet: string;
  bodyText: string;
  sentAt: string;
  clientId: string | null;
  clientMatch: ClientMatchStatus;
  matchCandidates: MatchCandidate[];
  classification: MessageClassification | null;
  attachments: MessageAttachment[];
  eventId: string | null;
}

export interface EmailDraftDto {
  id: string;
  version: number;
  status: EmailDraftStatus;
  mailboxId: string | null;
  threadId: string | null;
  replyToMessageId: string | null;
  clientId: string | null;
  to: MailAddressValue[];
  cc: MailAddressValue[];
  subject: string;
  bodyText: string;
  createdByType: EmailDraftAuthorType;
  createdBy: string | null;
  aiJobId: string | null;
  approvalId: string | null;
  sentAt: string | null;
  lastError: string | null;
}

// ═══ Usługi modułu poczty (Fala 4, `packages/core/src/modules/mail`, router `mail`) ═════════════════════

/** Pełna resynchronizacja po wygaśnięciu kursora (Gmail: 404 historyId) — tylko ostatnie N dni. */
export const MAIL_RESYNC_DAYS = 30;
/** Przebieg `running` starszy niż to = przerwany (restart workera); następny przebieg go zamyka. */
export const MAIL_SYNC_STALE_AFTER_SEC = 10 * 60;
/** „Synchronizuj teraz”: jedno zadanie na skrzynkę w oknie (ponowne kliknięcia nie mnożą przebiegów). */
export const MAIL_MANUAL_SYNC_WINDOW_SEC = 30;

// ── Historia skrzynki (17.10.2026: pierwsze połączenie i „Pobierz starsze maile”) ──

/**
 * „Pobierz starsze maile” (Ustawienia → Integracje → skrzynka, owner / admin; konsola `npm run admin -- mail-backfill`):
 * okno w dniach. Pierwsze połączenie skrzynki pobiera `MAIL_RESYNC_DAYS` (30) dni.
 */
export const MAIL_BACKFILL_DAYS = [30, 90, 365] as const;
export type MailBackfillDays = (typeof MAIL_BACKFILL_DAYS)[number];
/** Najwięcej wiadomości jednego pobrania historii (najnowsze z okna; INBOX i Wysłane razem). */
export const MAIL_BACKFILL_MAX_MESSAGES = 1000;
/** Wiadomości na jeden przebieg zadania (krótki przebieg trzyma blokadę skrzynki; reszta w kolejnym zadaniu). */
export const MAIL_BACKFILL_BATCH = 100;
/** Ile przebiegów jednego pobrania najwyżej (ochrona przed pętlą). */
export const MAIL_BACKFILL_MAX_CHUNKS = 30;
/** Synchronizacja nie działa dłużej niż tyle → element Inboxu dla właściciela skrzynki (raz na awarię). */
export const MAIL_SYNC_ALERT_AFTER_SEC = 60 * 60;

export const requestMailBackfillInput = z
  .object({
    mailboxId: idSchema,
    days: z.union([z.literal(30), z.literal(90), z.literal(365)], {
      errorMap: () => ({ message: 'Wybierz 30, 90 albo 365 dni' }),
    }),
  })
  .strict();
export type RequestMailBackfillInput = z.input<typeof requestMailBackfillInput>;

export interface RequestMailBackfillResult {
  queued: boolean;
  days: MailBackfillDays;
  simulated: boolean;
}

/** Stan skrzynki na serwerze (liczony przez workera przy synchronizacji; bez sekretów). */
export interface MailboxServerStats {
  /** wiadomości w Odebranych (INBOX) na serwerze; null = dostawca nie podaje */
  inbox: number | null;
  /** wiadomości w Wysłanych na serwerze; null = brak folderu albo dostawca nie podaje */
  sent: number | null;
  countedAt: string;
}

/** „Stan skrzynki” (Ustawienia → Integracje): na serwerze vs w systemie, ostatnia synchronizacja, ostatni błąd. */
export interface MailboxHealthView {
  mailboxId: string;
  address: string;
  status: MailboxStatus;
  simulated: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  /** ostatni nieudany przebieg (z błędem) — także po późniejszym udanym */
  lastFailure: { at: string; error: string } | null;
  /** nieudane przebiegi w ostatniej dobie */
  failuresLastDay: number;
  server: MailboxServerStats | null;
  imported: { received: number; sent: number; oldest: string | null; newest: string | null };
  /** trwające albo ostatnie pobieranie historii */
  backfill: {
    status: SyncRunStatus;
    startedAt: string;
    finishedAt: string | null;
    created: number;
    error: string | null;
  } | null;
  /** owner / admin: „Pobierz starsze maile” */
  canBackfill: boolean;
}

export const mailboxHealthInput = z.object({ integrationId: idSchema }).strict();

// ── Załączniki (tylko bezpieczne typy przez moduł plików; reszta jako metadane) ──

/** Największy załącznik pobierany automatycznie. */
export const MAIL_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Rozszerzenia pobierane automatycznie (podzbiór `FILE_TYPES` modułu plików; typ i tak sprawdzany po treści —
 * magic bytes). Archiwa (ZIP) i inne typy zostają metadanymi „pominięty załącznik”.
 */
export const MAIL_SAFE_ATTACHMENT_EXTENSIONS = [
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'webp',
  'txt',
  'csv',
  'md',
  'docx',
  'xlsx',
] as const;

function extensionOf(filename: string): string | null {
  const m = /\.([A-Za-z0-9]{1,10})$/.exec(filename.trim());
  return m?.[1] ? m[1].toLowerCase() : null;
}

/** Powód pominięcia załącznika przed pobraniem (null = pobieramy i sprawdzamy treść w module plików). */
export function mailAttachmentSkipReason(a: { filename: string; mime: string; sizeBytes: number }): string | null {
  const ext = extensionOf(a.filename);
  if (ext === 'zip' || /zip|compressed|x-rar|x-7z|x-tar/.test(a.mime)) {
    return 'Archiwum — nie pobieramy automatycznie';
  }
  if (!ext || !(MAIL_SAFE_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext)) {
    return 'Typ pliku nie jest pobierany automatycznie';
  }
  if (a.sizeBytes > MAIL_ATTACHMENT_MAX_BYTES) return 'Plik jest za duży (najwyżej 10 MB)';
  return null;
}

// ── Klasyfikacja (MVP §9, §14): reguły bez AI + wynik AI ──────────────────────

/** Pilne z AI tylko z wysoką pewnością; inaczej „Zwykłe” z flagą do przeglądu. */
export const MAIL_CRITICAL_MIN_CONFIDENCE = 0.85;

/** Słowa sygnalizujące możliwą awarię (reguły bez AI): „Zwykłe” + do przeglądu, nigdy Pilne bez potwierdzenia. */
export const MAIL_URGENT_KEYWORDS = [
  'awaria',
  'awarii',
  'nie działa',
  'nie dziala',
  'pilne',
  'pilnie',
  'utrata danych',
  'utraciliśmy dane',
  'straciliśmy dane',
] as const;

/**
 * Nagłówki brane pod uwagę przy istotności (17.10.2026): newsletter / lista (List-Unsubscribe, List-Id, Precedence),
 * automat (Auto-Submitted), priorytet nadany przez nadawcę (Importance, X-Priority, Priority, X-MSMail-Priority).
 * Adapter skrzynki podaje tylko te (nazwy małymi literami, wartości ≤ 200 znaków, bez znaków sterujących).
 */
export const MAIL_CLASSIFICATION_HEADERS = [
  'list-unsubscribe',
  'list-id',
  'precedence',
  'auto-submitted',
  'importance',
  'x-priority',
  'priority',
  'x-msmail-priority',
] as const;
export type MailClassificationHeader = (typeof MAIL_CLASSIFICATION_HEADERS)[number];
export type MailHeaderHints = Partial<Record<MailClassificationHeader, string>>;

const HEADER_VALUE_MAX = 200;

function headerText(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map(headerText).filter(Boolean).join(', ');
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

/**
 * Nagłówki do klasyfikacji z par [nazwa, wartość] (IMAP: `headerLines` mailparsera, Gmail: `payload.headers`). Pierwsze
 * wystąpienie nagłówka wygrywa; wartość bez znaków sterujących i zwiniętych linii.
 */
export function pickClassificationHeaders(entries: Iterable<readonly [string, unknown]>): MailHeaderHints {
  const out: MailHeaderHints = {};
  for (const [rawKey, rawValue] of entries) {
    const key = rawKey.trim().toLowerCase();
    if (!(MAIL_CLASSIFICATION_HEADERS as readonly string[]).includes(key)) continue;
    const name = key as MailClassificationHeader;
    if (out[name] !== undefined) continue;
    out[name] = headerText(rawValue)
      // biome-ignore lint/suspicious/noControlCharactersInRegex: nagłówki z internetu — bez znaków sterujących
      .replace(/[\u0000-\u001f\u007f]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, HEADER_VALUE_MAX);
  }
  return out;
}

const THANKS_PATTERN = /(^|[^\p{L}])(dzięki|dzieki|dziękuj\p{L}*|dziekuj\p{L}*|thanks|thank you|thx)($|[^\p{L}])/iu;
/** Nadawca sam mówi o wadze albo pilności („bardzo ważny”, „pilne”, „asap”); „ważność” (termin ważności) — nie. */
const DECLARED_WEIGHT_RE =
  /(^|[^\p{L}])(ważn(?!oś)\p{L}*|wazn(?!os)\p{L}*|pilne|pilnie|pilny|pilna|pilnego|pilnej|asap|na\s+wczoraj|priorytetow\p{L}*|niezwłocznie|niezwlocznie|urgent\p{L}*|important)($|[^\p{L}])/iu;
/** Wyraźna prośba albo pytanie do nas (także bez znaku zapytania). */
const REQUEST_RE =
  /(^|[^\p{L}])(prosz[ęe]|prosimy|prośb\p{L}*|prosb\p{L}*|potrzebuj\p{L}*|potrzebn\p{L}*|czy\s+(mo[żz]\p{L}*|m[oó]g\p{L}*|da\s+si[ęe]|jest|s[ąa]|b[ęe]dzie|b[ęe]d[ąa]|macie|masz|uda\s+si[ęe])|daj(cie)?\s+(mi\s+|nam\s+)?zna[ćc]|odpisz\p{L}*|odezwij\p{L}*|zadzwo[ńn]\p{L}*|please|could\s+you|can\s+you)($|[^\p{L}])/iu;
/** Sprawy, które zwykle wymagają uwagi: termin, pieniądze, reklamacja, spotkanie, umowa, wycena, zamówienie. */
const TOPIC_RE =
  /(^|[^\p{L}])(faktur\p{L}*|płatnoś\p{L}*|platnos\p{L}*|przelew\p{L}*|zapłat\p{L}*|zaplat\p{L}*|należnoś\p{L}*|naleznos\p{L}*|reklamacj\p{L}*|termin\p{L}*|deadline\p{L}*|spotkani\p{L}*|umow\p{L}*|wycen\p{L}*|zamówieni\p{L}*|zamowieni\p{L}*|invoice\p{L}*|payment\p{L}*|meeting\p{L}*)($|[^\p{L}])/iu;
/** Powiadomienie z formularza na stronie (nadawca często no-reply, a w treści pisze człowiek). */
const FORM_RE =
  /(formularz\p{L}*\s+(kontaktow\p{L}*|ze\s+strony|zapytani\p{L}*)|z\s+formularza|contact\s+form|form\s+submission|nowe\s+zapytanie\s+ze?\s+stron\p{L}*|nowa\s+wiadomo(ść|sc)\s+ze?\s+stron\p{L}*)/iu;
/** Nadawca-automat: no-reply, zwroty poczty, powiadomienia systemów, newslettery. */
const AUTOMATED_SENDER_RE =
  /^(no[-_.]?reply|do[-_.]?not[-_.]?reply|mailer[-_.]?daemon|postmaster|bounces?|notifications?|notify|alerts?|newsletters?|marketing|mailing|powiadomieni\p{L}*|automat\p{L}*|robot)([-+_.][^@]*)?@/iu;
/** Odpowiedź automatyczna i zwrot poczty po temacie. */
const AUTO_REPLY_SUBJECT_RE =
  /^\s*(automatic\s+reply|auto(matic)?[-\s]?reply|odpowied\p{L}*\s+automatyczn\p{L}*|automatyczn\p{L}*\s+odpowied\p{L}*|out\s+of\s+(the\s+)?office|nieobecno\p{L}*|undeliver\p{L}*|delivery\s+status\s+notification|mail\s+delivery\s+(failed|failure|subsystem|system)|returned\s+mail|niedostarcz\p{L}*)/iu;

function isBulk(h: MailHeaderHints): boolean {
  const precedence = (h.precedence ?? '').trim().toLowerCase();
  return (
    h['list-unsubscribe'] !== undefined ||
    h['list-id'] !== undefined ||
    precedence === 'bulk' ||
    precedence === 'list' ||
    precedence === 'junk'
  );
}

function senderMarkedImportant(h: MailHeaderHints): boolean {
  return (
    (h.importance ?? '').trim().toLowerCase().startsWith('high') ||
    /^[12](\D|$)/.test((h['x-priority'] ?? '').trim()) ||
    (h.priority ?? '').trim().toLowerCase().startsWith('urgent') ||
    (h['x-msmail-priority'] ?? '').trim().toLowerCase().startsWith('high')
  );
}

function autoSubmitted(h: MailHeaderHints): string | null {
  const v = (h['auto-submitted'] ?? '').trim().toLowerCase();
  return v && !v.startsWith('no') ? v : null;
}

function hasQuestion(text: string): boolean {
  return text.replace(/\bhttps?:\/\/\S+/gi, ' ').includes('?');
}

export interface RuleClassification {
  severity: MessageClassificationSeverity;
  reason: string;
  needsReview: boolean;
  /** istotność (osobny wymiar od „Pilne”): `false` = „Mniej ważne” */
  important: boolean;
  /**
   * nadawca sam zadeklarował wagę albo pilność (słowa w temacie lub treści, nagłówek priorytetu), poza newsletterami —
   * siatka bezpieczeństwa także przy wyniku AI (17.10.2026: test „bardzo ważny” bez powiadomienia)
   */
  declared: boolean;
  /** newsletter albo lista mailingowa (nagłówki) — AI nagłówków nie widzi, więc jego „istotny” tego nie zmienia */
  bulk: boolean;
}

export interface MailRuleInput {
  subject: string;
  bodyText: string;
  from?: { email: string } | null;
  headers?: MailHeaderHints | null;
  /** nadawca to kontakt klienta (dopasowanie po adresie) */
  fromClientContact?: boolean;
  /** odpowiedź w wątku, w którym pisaliśmy (In-Reply-To / References albo wątek z naszą wysłaną wiadomością) */
  replyToUs?: boolean;
}

/** Tekst bez cytowanych linii (`>`) i stopki „-- ”. */
function ownText(text: string): string {
  const lines: string[] = [];
  for (const line of text.split(/\r?\n/)) {
    if (line.trim() === '--') break;
    if (line.trimStart().startsWith('>')) continue;
    lines.push(line);
  }
  return lines.join('\n').trim();
}

/**
 * Klasyfikacja regułami (AI wyłączone, niedostępne albo błąd; DECISIONS 2026-10-17 [P] „Każda poczta w Inboxie, push
 * tylko o istotnych”). Ważność: słowa awarii → „Zwykłe” z flagą do przeglądu (Pilne nadaje tylko AI z wysoką pewnością
 * albo monitoring); reszta „Zwykłe” albo „Do wiadomości” według istotności. Istotność po kolei:
 *  1. newsletter / lista mailingowa (List-Unsubscribe, List-Id, Precedence bulk/list/junk) → mniej ważne,
 *  2. słowa awarii, nagłówek wysokiego priorytetu, nadawca pisze „ważne / pilne / asap” → istotny (`declared`),
 *  3. odpowiedź automatyczna, zwrot poczty → mniej ważne; automat (no-reply, mailer-daemon, Auto-Submitted,
 *     powiadomienia) → mniej ważne, chyba że to wiadomość z formularza na stronie,
 *  4. samo podziękowanie → mniej ważne,
 *  5. kontakt klienta, odpowiedź w naszym wątku, pytanie lub prośba, termin / płatność / spotkanie / umowa → istotny,
 *  6. pozostałe (wiadomość od osoby bez sygnałów) → istotny: reguły to zapas, awaria AI nie może chować poczty.
 */
export function classifyMessageByRules(m: MailRuleInput): RuleClassification {
  const body = ownText(m.bodyText);
  const text = `${m.subject}\n${body}`;
  const haystack = text.toLowerCase();
  const h = m.headers ?? {};
  const from = (m.from?.email ?? '').trim().toLowerCase();
  const urgent = MAIL_URGENT_KEYWORDS.find((k) => haystack.includes(k)) ?? null;
  const bulk = isBulk(h);
  const markedImportant = !bulk && senderMarkedImportant(h);
  const declaredWords = !bulk && DECLARED_WEIGHT_RE.test(text);
  const declared = !bulk && (Boolean(urgent) || markedImportant || declaredWords);
  const auto = autoSubmitted(h);
  const autoReply =
    (auto?.startsWith('auto-replied') ?? false) ||
    AUTO_REPLY_SUBJECT_RE.test(m.subject) ||
    /^(mailer[-_.]?daemon|postmaster)@/.test(from);
  const automated = autoReply || auto !== null || AUTOMATED_SENDER_RE.test(from);
  const short = body.replace(/\s+/g, ' ').trim();
  const thanksOnly = short.length > 0 && short.length <= 160 && THANKS_PATTERN.test(short) && !short.includes('?');

  const result = (important: boolean, reason: string, needsReview = false): RuleClassification => ({
    severity: important ? 'normal' : 'fyi',
    reason,
    needsReview,
    important,
    declared,
    bulk,
  });

  if (bulk) return result(false, 'Reguły: newsletter albo lista mailingowa — mniej ważne');
  if (urgent) {
    return result(
      true,
      `Reguły: w treści „${urgent}” — sprawdź, czy to pilne (bez potwierdzenia nie podnosimy do Pilne)`,
      true,
    );
  }
  if (markedImportant) return result(true, 'Reguły: nadawca oznaczył wiadomość jako ważną (wysoki priorytet)');
  if (declaredWords) return result(true, 'Reguły: nadawca pisze, że to ważne albo pilne');
  if (autoReply) return result(false, 'Reguły: odpowiedź automatyczna albo zwrot poczty — mniej ważne');
  if (automated) {
    if (FORM_RE.test(text)) return result(true, 'Reguły: wiadomość z formularza na stronie');
    return result(false, 'Reguły: wiadomość automatyczna (no-reply, powiadomienie systemu) — mniej ważne');
  }
  if (thanksOnly) return result(false, 'Reguły: podziękowanie bez prośby — mniej ważne');
  if (m.fromClientContact) return result(true, 'Reguły: nadawca to kontakt klienta');
  if (m.replyToUs) return result(true, 'Reguły: odpowiedź w wątku, w którym pisaliśmy');
  if (hasQuestion(text) || REQUEST_RE.test(text)) return result(true, 'Reguły: pytanie albo prośba');
  if (TOPIC_RE.test(text)) return result(true, 'Reguły: termin, płatność, spotkanie albo umowa');
  return result(true, 'Reguły: wiadomość od osoby');
}

/**
 * Definicja istotności dla AI (prompt klasyfikacji): osobny wymiar od „critical” (awaria). Wspólna dla haka Copilota
 * i `buildMailClassificationPrompt`.
 */
export const MAIL_IMPORTANT_DEFINITION =
  '"important": true, gdy mail wymaga uwagi osoby: nadawca sam pisze, że to ważne albo pilne („ważne”, „bardzo ważne”, „pilne”, „asap”, „proszę o szybki kontakt”), pyta albo prosi o coś, chodzi o termin, pieniądze (płatność, faktura), reklamację, spotkanie albo klient odpowiada w sprawie; false dla newsletterów, reklam, powiadomień systemów, automatycznych odpowiedzi i samych podziękowań. Istotność nie wymaga awarii — "critical" zostaje tylko dla awarii.';

/** Wynik klasyfikatora AI (hak `classifyIncoming` modułu Copilot). */
export interface AiClassificationCandidate {
  severity: MessageClassificationSeverity;
  reason: string;
  /** 0–1; brak = traktujemy jak niską pewność przy Pilne */
  confidence?: number | null;
  /** istotność z odpowiedzi AI; brak (starszy format odpowiedzi) = z ważności i reguł */
  important?: boolean | null;
  provider: string;
  simulated: boolean;
  aiJobId?: string | null;
}

/**
 * Klasyfikacja zapisywana w `messages.classification`: wynik AI (Pilne tylko z pewnością ≥
 * `MAIL_CRITICAL_MIN_CONFIDENCE`, inaczej „Zwykłe” do przeglądu) albo reguły. Istotność: Pilne zawsze; AI decyduje
 * polem `important`, ale newsletter z nagłówków jest mniej ważny, a mail, w którym nadawca sam pisze „ważne / pilne”,
 * jest istotny (reguły jako siatka bezpieczeństwa). Ważność wynika z istotności: istotny = „Zwykłe”, mniej ważny =
 * „Do wiadomości” (w Inboxie „Mniej ważne”).
 */
export function finalizeClassification(
  ai: AiClassificationCandidate | null,
  rules: RuleClassification,
  classifiedAt: Date,
): MessageClassification {
  const at = classifiedAt.toISOString();
  if (ai) {
    const reason = ai.reason.trim().slice(0, 400) || 'Klasyfikacja AI';
    const confidence = typeof ai.confidence === 'number' ? Math.max(0, Math.min(1, ai.confidence)) : null;
    const base = {
      provider: ai.provider,
      simulated: ai.simulated,
      aiJobId: ai.aiJobId ?? null,
      classifiedAt: at,
      confidence,
    };
    if (ai.severity === 'critical' && (confidence ?? 0) < MAIL_CRITICAL_MIN_CONFIDENCE) {
      return {
        ...base,
        severity: 'normal',
        reason: `${reason} (AI: możliwe Pilne, niska pewność — sprawdź)`,
        needsReview: true,
        important: true,
      };
    }
    if (ai.severity === 'critical') return { ...base, severity: 'critical', reason, important: true };
    let important = typeof ai.important === 'boolean' ? ai.important : ai.severity === 'normal' && rules.important;
    let note: string | null = null;
    if (rules.bulk && important) {
      important = false;
      note = 'reguły: newsletter albo lista mailingowa';
    } else if (rules.declared && !important) {
      important = true;
      note = 'reguły: nadawca pisze, że to ważne albo pilne';
    }
    return {
      ...base,
      severity: important ? 'normal' : 'fyi',
      reason: (note ? `${reason} (${note})` : reason).slice(0, 500),
      important,
      ...(rules.needsReview ? { needsReview: true } : {}),
    };
  }
  return {
    severity: rules.severity,
    reason: rules.reason,
    provider: 'rules',
    simulated: false,
    classifiedAt: at,
    important: rules.important,
    ...(rules.needsReview ? { needsReview: true } : {}),
  };
}

/**
 * Ważność zdarzenia `message.received` z klasyfikacji: Pilne zostaje, istotny = „Zwykłe”, mniej ważny = „Do
 * wiadomości” (widoczny w Inboxie jako „Mniej ważne”, od razu przeczytany). Brak klasyfikacji = „Zwykłe”.
 */
export function mailEventSeverity(c: MessageClassification | null): MessageClassificationSeverity {
  if (!c) return MESSAGE_UNCLASSIFIED_SEVERITY;
  if (c.severity === 'critical') return 'critical';
  return isMessageImportant(c) ? 'normal' : 'fyi';
}

/** Istotność wiadomości w widoku (po ręcznej zmianie obowiązuje ważność zdarzenia). */
export const MESSAGE_IMPORTANCE = ['critical', 'important', 'less'] as const;
export type MessageImportance = (typeof MESSAGE_IMPORTANCE)[number];

export function messageImportanceOf(severity: string | null | undefined): MessageImportance {
  if (severity === 'critical') return 'critical';
  return severity === 'fyi' ? 'less' : 'important';
}

// ── Wątki i temat ────────────────────────────────────────────────────────────

const SUBJECT_PREFIX = /^\s*((re|odp|fwd?|pd|aw|wg|sv|tr)\s*(\[\d+\])?\s*:\s*)+/i;

/** Temat do porównań wątków: bez „Re:”, „Odp:”, „Fwd:”, „PD:”, białe znaki zwinięte, małe litery. */
export function normalizeSubject(subject: string): string {
  return subject.replace(SUBJECT_PREFIX, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

/** Temat odpowiedzi: „Re: …” (bez podwajania prefiksu). */
export function replySubject(subject: string): string {
  const clean = subject.replace(SUBJECT_PREFIX, '').trim();
  return clean ? `Re: ${clean}` : 'Re:';
}

// ── Odbiorcy (Etap 5: „niejednoznaczny odbiorca blokuje wysyłkę”) ───────────

/**
 * Stan odbiorcy względem klienta szkicu: `ok` (kontakt tego klienta), `new_address` (adres spoza kontaktów),
 * `ambiguous` (kontakt kilku klientów), `other_client` (kontakt innego klienta). Wszystko poza `ok` blokuje wysyłkę do
 * jawnego potwierdzenia odbiorcy przez osobę (`confirmRecipients`).
 */
export const RECIPIENT_CHECK_STATUSES = ['ok', 'new_address', 'ambiguous', 'other_client'] as const;
export type RecipientCheckStatus = (typeof RECIPIENT_CHECK_STATUSES)[number];

export function recipientCheckStatus(draftClientId: string | null, clientIds: readonly string[]): RecipientCheckStatus {
  const unique = [...new Set(clientIds)];
  if (unique.length > 1) return 'ambiguous';
  if (unique.length === 0) return 'new_address';
  return unique[0] === draftClientId ? 'ok' : 'other_client';
}

export interface RecipientCheck {
  email: string;
  name: string | null;
  field: 'to' | 'cc';
  status: RecipientCheckStatus;
  /** klienci, u których adres jest aktywnym kontaktem (nazwy tylko klientów widocznych dla osoby) */
  clients: { id: string; name: string | null }[];
  needsConfirmation: boolean;
}

/** Odbiorcy wymagający potwierdzenia, których osoba jeszcze nie potwierdziła. */
export function unconfirmedRecipients(
  checks: readonly RecipientCheck[],
  confirmed: readonly string[],
): RecipientCheck[] {
  const ok = new Set(confirmed.map((e) => e.trim().toLowerCase()));
  return checks.filter((c) => c.needsConfirmation && !ok.has(c.email.toLowerCase()));
}

// ── Wejścia usług ────────────────────────────────────────────────────────────

export const MAIL_THREAD_FILTERS = ['all', 'unassigned'] as const;
export type MailThreadFilter = (typeof MAIL_THREAD_FILTERS)[number];

/** `mail.listThreads`: wątki (najnowsze najpierw), klient albo kolejka „Przypisz do klienta”. */
export const listMailThreadsInput = z
  .object({
    clientId: idSchema.optional(),
    filter: z.enum(MAIL_THREAD_FILTERS).default('all'),
    q: z.string().trim().max(200).optional(),
    limit: z.number().int().min(1).max(100).default(30),
    /** `<lastMessageAt ISO>|<id>` z poprzedniej strony */
    cursor: z.string().max(200).optional(),
  })
  .strict();
export type ListMailThreadsInput = z.input<typeof listMailThreadsInput>;

export const mailThreadIdInput = z.object({ id: idSchema }).strict();

/**
 * „Utwórz zadanie” z wiadomości (Figma 02, kompozycja Fali 4): pola zadania jak w „Nowe zadanie” + wiadomość źródłowa.
 * Zadanie dostaje pochodzenie „Z wiadomości” i link do wiadomości (`origin_ref`), karta zadania prowadzi do wątku.
 * Działa bez AI (zadania z treści maila przez AI: `copilot.tasksFromText`).
 */
export const createTaskFromMessageInput = taskFieldsSchema.extend({ messageId: idSchema }).strict();
export type CreateTaskFromMessageInput = z.input<typeof createTaskFromMessageInput>;

/** Przypisanie wątku do klienta (przenosi wszystkie wiadomości wątku, dopasowanie „Ręcznie”). */
export const assignMailThreadInput = z.object({ id: idSchema, clientId: idSchema }).strict();

/**
 * „Udostępnij zespołowi” / „Cofnij udostępnienie” wątku ze skrzynki osobistej (DECISIONS „Skrzynka osobista jest
 * prywatna”): tylko właściciel skrzynki, z UI (nie AI ani MCP), audyt. Udostępniony wątek widzą osoby z dostępem do
 * klienta; odpowiadać ze skrzynki osobistej nadal może tylko jej właściciel.
 */
export const shareMailThreadInput = z.object({ id: idSchema, shared: z.boolean() }).strict();
export type ShareMailThreadInput = z.infer<typeof shareMailThreadInput>;

/**
 * „Utwórz klienta z nadawcy” (tylko ręcznie): nowy klient + kontakt z adresu nadawcy, wątek przypisany. Fala 7:
 * opcjonalne dane firmy (NIP i dane z rejestru po „Pobierz dane”) — jak w „Nowy klient”.
 */
export const createClientFromSenderInput = z
  .object({
    id: idSchema,
    name: z.string().trim().min(1, { message: 'Podaj nazwę klienta' }).max(200),
    contactName: z.string().trim().max(200).optional(),
    company: clientCompanySchema.optional(),
  })
  .strict();

export const createEmailDraftInput = z
  .object({
    threadId: idSchema.nullable().optional(),
    replyToMessageId: idSchema.nullable().optional(),
    clientId: idSchema.nullable().optional(),
    mailboxId: idSchema.nullable().optional(),
    to: mailAddressListSchema.default([]),
    cc: mailAddressListSchema.default([]),
    subject: z.string().trim().max(998).default(''),
    bodyText: emailBodySchema.default(''),
  })
  .strict();
export type CreateEmailDraftInput = z.input<typeof createEmailDraftInput>;

/** Edycja szkicu (`version` z odczytu). Szkic czekający w Akceptacjach wraca do edycji, a zgoda jest wycofana. */
export const updateEmailDraftInput = z
  .object({
    id: idSchema,
    version: z.number().int().positive(),
    to: mailAddressListSchema.optional(),
    cc: mailAddressListSchema.optional(),
    subject: z.string().trim().max(998).optional(),
    bodyText: emailBodySchema.optional(),
  })
  .strict();
export type UpdateEmailDraftInput = z.input<typeof updateEmailDraftInput>;

export const emailDraftIdInput = z.object({ id: idSchema }).strict();

/** „Wyślij do akceptacji”: akceptacja `email.send` z dokładną treścią i potwierdzonymi odbiorcami. */
export const requestEmailSendInput = z
  .object({
    id: idSchema,
    version: z.number().int().positive(),
    confirmRecipients: z
      .array(emailSchema)
      .max(MAIL_MAX_RECIPIENTS * 2)
      .default([]),
    /**
     * „Wyślij bez klienta”: świadome potwierdzenie wysyłki odpowiedzi w wątku bez klienta (tylko osoba w aplikacji
     * z zakresem „Wszyscy klienci”; inni nadal przypisują klienta).
     */
    withoutClient: z.boolean().default(false),
  })
  .strict();
export type RequestEmailSendInput = z.input<typeof requestEmailSendInput>;

/** Sprawdzenie odbiorców na żywo w kompozytorze (bez zapisu). */
export const checkMailRecipientsInput = z
  .object({
    clientId: idSchema.nullable(),
    to: mailAddressListSchema.default([]),
    cc: mailAddressListSchema.default([]),
  })
  .strict();

export const syncMailNowInput = z.object({ mailboxId: idSchema.optional() }).strict();

/** Narzędzie dev „Symuluj przychodzącą wiadomość” (FakeMailbox, tylko development, owner/admin). */
export const simulateIncomingMailInput = z
  .object({
    mailboxId: idSchema.optional(),
    from: mailAddressSchema,
    subject: emailSubjectSchema,
    bodyText: z.string().trim().min(1, { message: 'Treść jest wymagana' }).max(20_000),
    /** odpowiedź w istniejącym wątku */
    threadId: idSchema.optional(),
  })
  .strict();
export type SimulateIncomingMailInput = z.input<typeof simulateIncomingMailInput>;

/**
 * „Przygotuj odpowiedź z AI” (hak Copilota; bez niego — czytelny stan „niedostępne”). Szkic AI do ostatniej wiadomości
 * wątku jest jeden: kolejne „Przygotuj” zwraca istniejący, a nowy powstaje tylko po jawnym „Przygotuj nowy”
 * (`regenerate`, poprzedni szkic anulowany, jego zgoda wycofana).
 */
export const draftReplyWithAiInput = z
  .object({
    threadId: idSchema,
    instructions: z.string().trim().max(1000).optional(),
    regenerate: z.boolean().optional(),
  })
  .strict();

export type DraftReplyWithAiInput = z.input<typeof draftReplyWithAiInput>;

/** „Sprawdziłem w Wysłanych: maila nie ma” / „Odbiorca potwierdził: maila nie dostał” (wynik nieznany, ręczna weryfikacja). */
export const confirmEmailNotSentInput = z.object({ id: idSchema, version: z.number().int().positive() }).strict();

// ── Wyjścia usług ────────────────────────────────────────────────────────────

export interface MailClientRef {
  id: string;
  name: string;
}

export interface MailAttachmentView extends MessageAttachment {
  /** REST `/api/files/:id` (tylko zapisany plik) */
  downloadPath: string | null;
}

export interface MailMessageView {
  id: string;
  direction: MessageDirection;
  from: MailAddressValue;
  to: MailAddressValue[];
  cc: MailAddressValue[];
  subject: string;
  /** tylko tekst (HTML maila zamieniony na bezpieczny tekst po stronie serwera; nigdy nie renderujemy HTML) */
  bodyText: string;
  sentAt: string;
  clientId: string | null;
  clientMatch: ClientMatchStatus;
  classification: MessageClassification | null;
  /**
   * odebrana: Pilne / Istotne / Mniej ważne według obowiązującej ważności zdarzenia (po ręcznej zmianie); wysłana: null
   */
  importance?: MessageImportance | null;
  attachments: MailAttachmentView[];
  eventId: string | null;
}

export type EmailDraftApprovalState =
  | 'pending'
  | 'approved'
  | 'executing'
  | 'succeeded'
  | 'failed'
  | 'rejected'
  | 'expired'
  | null;

/** Źródło kontekstu szkicu AI (bez treści): typ, tytuł, data; `untrusted` = dane klienta. */
export interface EmailDraftAiSource {
  layer: string;
  type: string;
  id: string | null;
  title: string;
  date?: string | null;
  untrusted?: boolean;
}

/**
 * Szkic przygotowany przez AI (Figma 02 „Przygotuj”, DESIGN §2 zasada 5, MVP §11): dostawca i oznaczenie symulacji,
 * źródła, jawne założenia, pytania do wyjaśnienia i ostrzeżenia (np. polecenia w treści maila). Zapisane przy szkicu,
 * więc widać je także po ponownym otwarciu.
 */
export interface EmailDraftAiNotes {
  provider: string;
  model: string | null;
  simulated: boolean;
  generatedAt: string;
  assumptions: string[];
  questions: string[];
  warnings: string[];
  sources: EmailDraftAiSource[];
}

export interface EmailDraftView extends EmailDraftDto {
  createdAt: string;
  updatedAt: string;
  /** szkic z „Przygotuj odpowiedź z AI”: źródła, założenia, pytania, ostrzeżenia, symulacja */
  ai: EmailDraftAiNotes | null;
  /** wysyłka z nieznanym wynikiem: sprawdź w skrzynce (`sendCheck`), zanim wyślesz ponownie */
  sendOutcomeUnknown: boolean;
  /** jak sprawdzić ręcznie wysyłkę z nieznanym wynikiem (zależy od skrzynki); `null` = nie dotyczy */
  sendCheck: MailSendCheck | null;
  /** stan akceptacji szkicu (czekający, wykonywany, błąd — także „wymaga ręcznej weryfikacji”) */
  approvalState: EmailDraftApprovalState;
  approvalError: string | null;
  manualCheckRequired: boolean;
  canEdit: boolean;
}

export interface MailSyncRunView {
  status: SyncRunStatus;
  trigger: SyncRunTrigger;
  startedAt: string;
  finishedAt: string | null;
  fetched: number;
  created: number;
  duplicates: number;
  skipped: number;
  error: string | null;
  simulated: boolean;
}

/** Stan skrzynki: „Ostatnia synchronizacja: …”, baner „Utracono dostęp do skrzynki”, tryb symulacji. */
export interface MailboxStatusView {
  id: string;
  address: string;
  status: MailboxStatus;
  integrationId: string;
  integrationStatus: string;
  simulated: boolean;
  lastSyncAt: string | null;
  lastError: string | null;
  /**
   * utracona autoryzacja (token wygasł albo cofnięty) — wymaga ponownego połączenia; integracja „Utracono
   * autoryzację”, a skrzynka w symulacji: ostatni przebieg skończył się utratą autoryzacji
   */
  authLost: boolean;
  lastRun: MailSyncRunView | null;
}

export interface MailStatusResult {
  mailboxes: MailboxStatusView[];
  canSync: boolean;
  /** narzędzie dev „Symuluj przychodzącą wiadomość” (development, owner/admin) */
  canSimulate: boolean;
}

/**
 * Prywatność wątku (skrzynka osobista „Twoje”): `personal` = skrzynka osobista, `mine` = moja, `shared` = udostępniony
 * zespołowi, `canShare` = mogę udostępnić / cofnąć (tylko właściciel skrzynki). Skrzynka firmowa: wszystko false.
 */
export interface MailThreadPrivacy {
  personal: boolean;
  mine: boolean;
  /** właściciel cudzej skrzynki osobistej (wątek udostępniony); przy własnej null */
  ownerName: string | null;
  shared: boolean;
  canShare: boolean;
}

export interface MailThreadListItem {
  id: string;
  subject: string;
  client: MailClientRef | null;
  lastMessageAt: string;
  messageCount: number;
  lastFrom: MailAddressValue;
  lastDirection: MessageDirection;
  snippet: string;
  /** wątek bez klienta: dopasowanie ostatniej wiadomości (kolejka „Przypisz do klienta”) */
  unresolved: 'ambiguous' | 'unknown' | null;
  /** najnowszy otwarty szkic w wątku */
  draftStatus: EmailDraftStatus | null;
  simulated: boolean;
  /** wątek ze skrzynki osobistej: `private` = widzi tylko właściciel, `shared` = udostępniony zespołowi; firmowa: null */
  privacy: 'private' | 'shared' | null;
}

export interface MailThreadListPage {
  items: MailThreadListItem[];
  nextCursor: string | null;
}

export interface MailMatchCandidateView {
  clientId: string;
  clientName: string;
  contactName: string | null;
}

export interface MailThreadDetail {
  id: string;
  subject: string;
  mailbox: MailboxStatusView;
  client: MailClientRef | null;
  unresolved: 'ambiguous' | 'unknown' | null;
  candidates: MailMatchCandidateView[];
  /** druga strona rozmowy (nadawca ostatniej przychodzącej) — „Utwórz klienta z nadawcy” */
  counterpart: MailAddressValue | null;
  messages: MailMessageView[];
  drafts: EmailDraftView[];
  /** wartości domyślne kompozytora „Odpowiedz” */
  reply: { replyToMessageId: string; to: MailAddressValue[]; cc: MailAddressValue[]; subject: string } | null;
  permissions: { canReply: boolean; canAssign: boolean; canCreateClient: boolean };
  /** „Przygotuj odpowiedź z AI”: dostępne, gdy Copilot zarejestrował hak i AI jest włączone */
  ai: { available: boolean; reason: string | null };
  simulated: boolean;
  privacy: MailThreadPrivacy;
}

export interface MailSyncResult {
  status: 'succeeded' | 'failed' | 'skipped';
  runId: string | null;
  fetched: number;
  created: number;
  duplicates: number;
  /** wiadomości, których nie da się zapisać (błąd danych) — pominięte, kursor idzie dalej */
  skipped: number;
  error: string | null;
  authLost: boolean;
  resetRequired: boolean;
  simulated: boolean;
}

export interface SyncMailNowResult {
  queued: number;
  simulated: boolean;
}

export interface RequestEmailSendResult {
  approvalId: string;
  draft: EmailDraftView;
  /** wysyłka na adapterze fake: „Symulacja — nic nie zostanie wysłane” */
  simulated: boolean;
  /**
   * Prośba agenta (AI, MCP): odbiorcy spoza kontaktów klienta, których musi potwierdzić zatwierdzający (Fala 6).
   * Osoba w aplikacji potwierdza ich sama w kompozytorze, więc u niej zawsze pusta lista.
   */
  recipientsToConfirm: string[];
  /**
   * Osoba w aplikacji z prawem do wysyłki („Wysyła maile”, moduł Akceptacje: Edycja): zgoda zapisana od razu w tym
   * samym żądaniu (audyt „zatwierdzone przy wysyłce”), mail wychodzi przez silnik akceptacji bez czekania.
   * `false` = czeka w Akceptacjach (brak prawa albo prośba agenta).
   */
  approvedNow: boolean;
}

/** Podpis skrzynki (Ustawienia → Integracje → skrzynka): zapis HTML po sanitizacji w konfiguracji integracji. */
export const setMailboxSignatureInput = z
  .object({
    integrationId: idSchema,
    /** pusty tekst = usuń podpis */
    signatureHtml: z.string().max(MAIL_SIGNATURE_INPUT_MAX_CHARS, MAIL_SIGNATURE_MESSAGES.inputTooLong),
  })
  .strict();
export type SetMailboxSignatureInput = z.input<typeof setMailboxSignatureInput>;

export interface MailboxSignatureView {
  integrationId: string;
  /** podpis po sanitizacji (`null` = brak) */
  signatureHtml: string | null;
}

export interface SimulateIncomingMailResult {
  messageId: string;
  threadId: string;
  created: boolean;
  severity: MessageClassificationSeverity;
  clientMatch: ClientMatchStatus;
}

export interface DraftReplyWithAiResult {
  state: 'ok' | 'unavailable' | 'disabled' | 'budget_exceeded' | 'failed';
  message: string | null;
  draft: EmailDraftView | null;
  simulated: boolean;
  /** zwrócony istniejący szkic AI do tej wiadomości (bez nowego wywołania AI) */
  existing: boolean;
}

export const MAIL_MESSAGES = {
  threadNotFound: 'Nie ma tego wątku albo nie masz do niego dostępu.',
  noMailbox: 'Brak skrzynki do wysyłki. Podłącz pocztę w Ustawieniach → Integracje.',
  mailboxUnavailable: 'Skrzynka nie działa (utracono autoryzację albo błąd). Połącz ją ponownie.',
  mailboxWorkerOnly: 'Skrzynka z prawdziwym kontem działa tylko w workerze.',
  draftNotEditable: 'Tego szkicu nie można już zmienić.',
  draftSent: 'Ten mail został już wysłany.',
  sendPending: 'Ten szkic czeka już w Akceptacjach.',
  sendStale: 'Szkic zmienił się po prośbie o wysyłkę. Wyślij go do akceptacji ponownie.',
  recipientsNeedConfirmation: 'Potwierdź odbiorców spoza kontaktów klienta, zanim wyślesz mail do akceptacji.',
  agentRecipientsNote:
    'Odbiorcę spoza kontaktów klienta wskazał agent — zatwierdzający musi go jawnie potwierdzić na karcie akceptacji.',
  confirmAgentRecipient: 'Potwierdzam odbiorcę',
  recipientsChanged: 'Odbiorcy wymagają ponownego potwierdzenia — nic nie zostało wysłane.',
  clientRequired: 'Przypisz wiadomość do klienta przed wysłaniem.',
  withoutClientForbidden:
    'Bez klienta wysyła tylko osoba z dostępem do wszystkich klientów. Przypisz wątek do klienta.',
  withoutClientThreadOnly: 'Bez klienta można wysłać tylko odpowiedź w wątku.',
  sentNow: 'Mail wysłany',
  sentNowSimulated: 'Symulacja — nic nie zostało naprawdę wysłane.',
  queuedForApproval: 'Mail czeka w Akceptacjach',
  subjectRequired: 'Temat jest wymagany',
  bodyRequired: 'Treść jest wymagana',
  replyOtherThread: 'Wiadomość, na którą odpowiadasz, jest w innym wątku.',
  clientMismatch: 'Szkic w wątku musi dotyczyć klienta wątku.',
  simulationDevOnly: 'Symulacja poczty działa tylko w środowisku deweloperskim.',
  simulationAdminOnly: 'Symulować wiadomości może właściciel albo admin.',
  simulationFakeOnly: 'Symulacja działa tylko na skrzynce w trybie symulacji.',
  authLostBody:
    'Połącz skrzynkę ponownie w Ustawieniach → Integracje. Do tego czasu nowe maile nie są pobierane, a wysyłka jest wstrzymana.',
  sendIrreversible: 'Wysłanego maila nie da się cofnąć.',
  simulatedSend: 'Symulacja — nic nie zostało wysłane',
  syncQueued: 'Synchronizacja w toku. Nowe maile pojawią się za chwilę.',
  draftWithdrawn: 'Szkic zmieniony po prośbie o wysyłkę — poprzednia zgoda wycofana.',
  aiUnavailable: 'Copilot nie jest jeszcze dostępny. Odpowiedz ręcznie.',
  aiDisabled: 'AI jest wyłączone. Odpowiedz ręcznie.',
  aiFailed: 'Nie udało się przygotować odpowiedzi. Spróbuj ponownie albo odpowiedz ręcznie.',
  aiDraftExists: 'Szkic AI do tej wiadomości już jest — otwieram go. Nowy przygotujesz przyciskiem „Przygotuj nowy”.',
  aiDraftReplaced: 'Zastąpiony nowym szkicem AI',
  sendUnknown:
    'Nie wiadomo, czy mail wyszedł (brak odpowiedzi skrzynki). Sprawdź folder Wysłane, zanim wyślesz go ponownie.',
  notSendUnknown: 'Ten szkic nie czeka na sprawdzenie wysyłki.',
  confirmedNotSent: 'Sprawdzone: mail nie wyszedł. Możesz wysłać go do akceptacji ponownie.',
  personalMailboxForeign: 'To skrzynka osobista innej osoby — wysłać z niej może tylko jej właściciel.',
  personalMailboxApprover: 'Wysyłkę z osobistej skrzynki zatwierdza tylko jej właściciel.',
  personalMailboxReplyHint: 'Odpowiada właściciel skrzynki — wątek jest z jego skrzynki osobistej.',
  shareOwnerOnly: 'Udostępnić wątek może tylko właściciel skrzynki osobistej.',
  shareCompanyMailbox: 'Wątki skrzynki firmowej widzi zespół (według zakresu klientów) — nie trzeba ich udostępniać.',
  privateThreadHint: 'Skrzynka osobista — ten wątek widzisz tylko Ty.',
  sharedThreadHint: 'Udostępniony zespołowi — widzą go osoby z dostępem do klienta.',
  messageSkippedTitle: 'Nie udało się zapisać wiadomości',
  backfillAdminOnly: 'Starsze maile pobiera właściciel albo administrator.',
  backfillQueued: 'Pobieranie starszych maili w toku. Pojawią się w Inboxie jako przeczytane, bez powiadomień.',
  backfillPaused: 'Skrzynka jest wstrzymana — wznów ją, zanim pobierzesz starsze maile.',
  backfillUnsupported: 'Ta skrzynka nie obsługuje pobierania historii.',
  backfillBusy: 'Skrzynka była zbyt długo zajęta, więc pobieranie starszych maili przerwano. Zleć je ponownie.',
  importedReason: 'Pobrane z historii skrzynki — mniej ważne, bez powiadomień',
  syncStaleBody:
    'Od ponad godziny nie udaje się pobrać nowych maili. Wiadomości czekają na serwerze — pobierzemy je, gdy połączenie wróci. Sprawdź Ustawienia → Integracje → Stan skrzynki.',
} as const;

/**
 * Ręczne sprawdzenie wysyłki z nieznanym wynikiem (przegląd Fali 8; Fala 9 — DECISIONS 2026-10-07 [S] „Poczta jak
 * w zwykłej skrzynce”): gdzie szukać maila, zależy od skrzynki. Gmail sam zapisuje kopię w Wysłanych. Skrzynka
 * IMAP/SMTP (np. Zenbox) dostaje kopię w Wysłanych dopiero po potwierdzeniu serwera SMTP, bez kopii do siebie
 * w Odebranych — po braku odpowiedzi serwera kopii nie ma nigdzie, nawet gdy mail wyszedł, więc pewność daje tylko
 * odbiorca.
 */
export const MAIL_SEND_CHECKS = ['sent_folder', 'ask_recipient'] as const;
export type MailSendCheck = (typeof MAIL_SEND_CHECKS)[number];

export const MAIL_SEND_CHECK_TEXTS: Record<MailSendCheck, { hint: string; confirm: string }> = {
  sent_folder: { hint: MAIL_MESSAGES.sendUnknown, confirm: 'Sprawdziłem w Wysłanych: maila nie ma' },
  ask_recipient: {
    hint: 'Nie wiadomo, czy mail wyszedł (serwer poczty nie potwierdził wysyłki). Kopia w Wysłanych powstaje dopiero po potwierdzeniu, więc jej brak nie oznacza, że mail nie wyszedł — zapytaj odbiorcę, czy go dostał, zanim wyślesz go ponownie.',
    confirm: 'Odbiorca potwierdził: maila nie dostał',
  },
};

/** Sposób sprawdzenia dla dostawcy skrzynki (`integrations.provider`). */
export function mailSendCheckFor(provider: string | null | undefined): MailSendCheck {
  return provider === 'imap' ? 'ask_recipient' : 'sent_folder';
}

export function mailSyncStaleTitle(address: string): string {
  return `Poczta ${address} nie synchronizuje się`;
}

export function mailAuthLostTitle(address: string): string {
  return `Utracono dostęp do skrzynki ${address}`;
}
